import { VNode, VueConstructor } from 'vue';
import { ResponsiveColumn } from '@ks/ks-element-ui/types/col';
import { Form, FormItem } from '@ks/ks-element-ui';
import { SetupContext } from '@vue/composition-api';
import ConfForm from '@/components/form/form';
import { ZRuleItem, ZFormNode } from '@ks/z-form';

export const inheritFormItemProps = [
    'labelWidth',
    'required',
    'error',
    'showMessage',
    'inlineMessage',
    'size',
] as const;

export type ConfFormItem<T = any> = {
    /**
     * 校验规则
     */
    rules?: ZRuleItem[];
    /**
     * 校验规则，影响提交按钮状态
     */
    requiredRules?: ZRuleItem[];
    /**
     * 字段名
     */
    field?: string;
    /**
     * 字段文案
     */
    label?: VNode | string;
    /**
     * 表单项
     *
     */
    render:
        | SetupFunction<{
              value: T;
              form: InstanceType<typeof ConfForm>;
              node: ZFormNode;
          }>
        | VueConstructor;
    /**
     * 表单项布局
     */
    col?: Partial<ResponsiveColumn>;
    /**
     * 是否展示
     */
    show?: boolean;
    /**
     * 表单项初值，只有当表单项构造时 value 为 undefined 时才会赋值
     */
    initialValue?: () => Record<string, any>;
} & Partial<
    Pick<InstanceType<typeof FormItem>, typeof inheritFormItemProps[number]>
>;

export const inheritFormProps = [
    'labelPosition',
    'labelWidth',
    'labelSuffix',
    'statusIcon',
    'size',
] as const;

export interface ConfFormProps
    extends Partial<
        Pick<InstanceType<typeof Form>, typeof inheritFormProps[number]>
    > {
    /**
     * 表单配置项
     */
    options: ConfFormOptions;

    /**
     * 表单提交事件
     * @param val 表单数据
     */
    onSubmit(val: Record<string, any>): void;
    /**
     * 表单初值
     * vue 2 只能 mutate 修改原始对象，重置时会调用该函数获取新初始值
     */
    initialValue: () => Record<string, any>;
    /**
     * 表单项列数，优先级低于表单配置项内 col
     */
    colCount?: number;
    /**
     * 行内布局，开启后 col 相关配置失效
     */
    inline?: boolean;
}

export interface ConfFormOption extends ConfFormItem {
    value?: any;
}

export type ConfFormOptions = ConfFormOption[];

export type ConfFormOptionsFactory<T extends any[] = any> = (
    ...args: T
) => ConfFormOptions;

type OptionalPropertyOf<T extends object> = {
    [K in keyof T]: T extends Record<K, T[K]> ? never : K;
}[keyof T];

export type SpecificPropsType<
    T extends Record<string, any>,
    OptionalK = OptionalPropertyOf<T>,
> = Required<{
    [K in keyof T]: {
        type: () => T[K];
        required: K extends OptionalK ? false : true;
    };
}>;

declare type SetupFunction<Props> = (
    this: void,
    props: Props,
    ctx: SetupContext & {
        emit: (e: 'change', ...p: any[]) => void;
    },
) => VNode | null;
